import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, FileText, MessageSquare, CalendarClock, Bookmark, Star, ShieldCheck } from "lucide-react";

export default function CandidateProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [resume, setResume] = useState(null);
  const [msg, setMsg] = useState("");
  const [showMsg, setShowMsg] = useState(false);

  useEffect(() => {
    api.get(`/search/candidate/${id}`).then((r) => setP(r.data))
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); nav("/employer"); });
  }, [id]);

  const viewResume = async () => {
    try { const { data } = await api.get(`/search/candidate/${id}/resume`); setResume(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async () => { try { await api.post("/search/save", { candidate_id: id }); toast.success("Saved"); } catch { toast.error("Failed"); } };
  const sendMsg = async () => {
    if (!msg.trim()) return;
    try { await api.post("/messages/start", { candidate_id: id, body: msg }); toast.success("Message sent"); setShowMsg(false); setMsg(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const requestInterview = async () => {
    try { await api.post("/messages/interview-request", { candidate_id: id, message: "We'd like to schedule an interview." }); toast.success("Interview requested"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!p) return <div className="min-h-screen grid place-items-center text-muted font-mono text-sm">Loading profile…</div>;

  return (
    <div className="min-h-screen">
      <header className="ta-glass border-b border-white/10 sticky top-0 z-40">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <button onClick={() => nav("/employer")} className="inline-flex items-center gap-2 text-sm text-muted hover:text-ink transition-colors duration-200"><ArrowLeft size={16} /> Back</button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <main className="lg:col-span-8 space-y-6">
          <div className="rounded-2xl border border-white/10 bg-surface p-7">
            <div className="flex items-center gap-3">
              <h1 className="font-display font-bold text-3xl">{p.display_name}</h1>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald/10 px-2.5 py-1 text-[11px] text-emerald"><BadgeCheck size={12} /> {(p.verification_level || "").replace(/_/g, " ")}</span>
              {p.open_to_offers && <span className="inline-flex items-center gap-1 rounded-full bg-azure/10 px-2.5 py-1 text-[11px] text-azure">Open to offers</span>}
              {p.references_count > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-emerald/10 px-2.5 py-1 text-[11px] text-emerald" data-testid="references-badge"><ShieldCheck size={12} /> {p.references_count} reference{p.references_count > 1 ? "s" : ""}</span>}
            </div>
            <p className="mt-1 text-muted">{p.headline}</p>
            <p className="mt-2 text-sm text-faint font-mono">{p.country} · prefers {p.preferred_location} · {p.years_experience} yrs · {p.relocation ? "open to relocation" : "no relocation"} · {p.availability}</p>
            {p.executive_summary && <p className="mt-4 text-sm text-ink/90 leading-relaxed">{p.executive_summary}</p>}
          </div>

          <Section title="Verified skills">
            <div className="flex flex-wrap gap-2">
              {(p.skills || []).map((s, i) => <span key={i} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-ink">{s.name}<span className="text-faint text-xs ml-2 font-mono">{s.proficiency}</span></span>)}
            </div>
          </Section>

          <Section title="Project evidence">
            <div className="space-y-3">
              {(p.projects || []).map((pr, i) => (
                <div key={i} className="rounded-xl border border-white/10 p-4">
                  <p className="font-display font-bold text-ink">{pr.title}</p>
                  <p className="text-sm text-muted mt-1">{pr.problem}</p>
                  <p className="text-xs text-emerald font-mono mt-2">{pr.impact}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">{(Array.isArray(pr.technologies) ? pr.technologies : []).map((t, j) => <span key={j} className="rounded bg-white/5 px-2 py-0.5 text-[10px] font-mono text-muted">{t}</span>)}</div>
                </div>
              ))}
            </div>
          </Section>

          {p.achievements?.length > 0 && <Section title="Achievements"><ul className="space-y-1.5 text-sm text-muted list-disc pl-5">{p.achievements.map((a, i) => <li key={i}>{a}</li>)}</ul></Section>}

          {p.publications?.length > 0 && (
            <Section title="Publications">
              <ul className="space-y-2.5" data-testid="publications-section">
                {p.publications.map((pub, i) => {
                  const title = typeof pub === "string" ? pub : pub.title;
                  const url = typeof pub === "object" ? pub.url : null;
                  const year = typeof pub === "object" ? pub.year : null;
                  const journal = typeof pub === "object" ? pub.journal : null;
                  return (
                    <li key={i} className="text-sm" data-testid={`publication-${i}`}>
                      {url ? <a href={url} target="_blank" rel="noreferrer" className="text-ink hover:text-azure hover:underline transition-colors duration-200">{title}</a> : <span className="text-ink">{title}</span>}
                      {(journal || year) && <span className="text-faint text-xs font-mono ml-2">{[journal, year].filter(Boolean).join(" · ")}</span>}
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}

          {p.references?.length > 0 && (
            <Section title="References">
              <div className="space-y-4" data-testid="references-section">
                {p.references.map((r, i) => (
                  <div key={i} className="rounded-xl border border-white/10 p-4" data-testid={`reference-card-${i}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-display font-bold text-ink">{r.name}<span className="text-faint font-normal text-sm ml-2">{r.relationship}</span></p>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} className={n <= (r.rating || 0) ? "fill-amber text-amber" : "text-white/20"} />)}
                      </div>
                    </div>
                    <p className="text-sm text-muted italic mt-2">“{r.testimonial}”</p>
                    {r.would_work_again && <p className="text-xs text-emerald mt-2 flex items-center gap-1"><BadgeCheck size={12} /> Would work with them again</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title="Evidence graph">
            <div className="font-mono text-xs text-muted space-y-1.5">
              {p.evidence_graph?.nodes?.filter((n) => n.type !== "root").slice(0, 12).map((n) => (
                <p key={n.id}>→ <span className={n.type === "skill" ? "text-azure" : "text-emerald"}>{n.label}</span> <span className="text-faint">({n.type})</span></p>
              ))}
            </div>
          </Section>
        </main>

        <aside className="lg:col-span-4">
          <div className="rounded-2xl border border-white/10 bg-surface p-6 sticky top-24 space-y-2.5">
            <button onClick={viewResume} data-testid="view-resume-btn" className="w-full inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-ink hover:bg-white/5 transition-colors duration-200"><FileText size={15} /> View authorized résumé</button>
            <button onClick={save} data-testid="profile-save-btn" className="w-full inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-ink hover:bg-white/5 transition-colors duration-200"><Bookmark size={15} /> Save candidate</button>
            <button onClick={() => setShowMsg(!showMsg)} data-testid="message-btn" className="w-full inline-flex items-center gap-2 rounded-full bg-azure px-4 py-2.5 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200"><MessageSquare size={15} /> Message</button>
            <button onClick={requestInterview} data-testid="interview-btn" className="w-full inline-flex items-center gap-2 rounded-full bg-emerald px-4 py-2.5 text-sm font-medium text-black hover:bg-white transition-colors duration-200"><CalendarClock size={15} /> Request interview</button>
            {showMsg && (
              <div className="pt-2">
                <textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Your message…" data-testid="message-input"
                  className="w-full rounded-lg border border-white/12 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none" />
                <button onClick={sendMsg} data-testid="message-send" className="mt-2 w-full rounded-full bg-azure py-2 text-sm text-white hover:bg-white hover:text-black transition-colors duration-200">Send</button>
              </div>
            )}
            {resume && (
              <div className="mt-3 rounded-lg border border-white/10 bg-base p-3 max-h-64 overflow-y-auto">
                <p className="text-[11px] text-faint mb-2">{resume.note}</p>
                <p className="text-xs text-muted whitespace-pre-wrap">{resume.resume_text}</p>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-surface p-7">
      <p className="label-mono text-azure mb-4">{title}</p>
      {children}
    </div>
  );
}
